import * as React from "react";
import axios from "axios";
import {jwtDecode} from "jwt-decode";

class ApiHelper {
    endpointsBase = '';
    accessToken = null;

    constructor(endpointsBase) {
        this.endpointsBase = endpointsBase;

        this.apiUrls = {
            auth: {
                login: '/auth/login',
                loginWithGoogle: '/auth/login-with-google',
                register: '/auth/register',
                forgot: '/auth/forgot-password',
                forgotConfirm: '/auth/forgot-password/confirm',
                logout: '/auth/logout',
                refreshToken: '/auth/refresh',
                selfUpdateToken: '/auth/self-update',
            },
            users: {
                list: '/users/list',
                view: '/users/:id',
            },
        };
    }


    async authLogin(credentials) {
        let url = this.apiUrls.auth.login;
        return this.postRequest(url, credentials);
    }


    async authLoginWithGoogle(credentials) {
        let url = this.apiUrls.auth.loginWithGoogle;
        return this.postRequest(url, credentials);
    }


    async authForgotPassword(credentials) {
        let url = this.apiUrls.auth.forgot;
        return this.postRequest(url, credentials);
    }


    async authForgotPasswordConfirm(credentials) {
        let url = this.apiUrls.auth.forgotConfirm;
        return this.postRequest(url, credentials);
    }


    async authRegister(credentials) {
        let url = this.apiUrls.auth.register;
        return this.postRequest(url, credentials);
    }


    /**
     * Make GET request to API endpoint
     * @param {string} url a relative API endpoint URL
     * @returns {Object|null} JSON-decoded response
     */
    async getRequest(url) {
        let fullUrl = (url.indexOf('https://') !== -1)?(url):(this.endpointsBase + url),
            requestParams = {
                url: fullUrl,
                headers: {
                    'Accept': 'application/json, text/plain, */*'
                }
            };

        return await this._request(requestParams);
    }


    /**
     * Make POST request to API endpoint
     * @param {string} url a relative API endpoint URL
     * @param {Object|null} data data to send
     * @returns {Promise|null} JSON-decoded response
     */
    async postRequestRaw(url, data = null) {
        let fullUrl = (url.indexOf('https://') !== -1)?(url):(this.endpointsBase + url),
            requestParams = {
                method: 'POST',
                url: fullUrl,
                headers: {
                    'Accept': 'application/json, text/plain',
                    'Content-Type': 'multipart/form-data; charset=UTF-8'
                },
                data: data
            };

        return await this._request(requestParams);
    }


    /**
     * Make POST request to API endpoint
     * @param {string} url a relative API endpoint URL
     * @param {Object|null} data data to send
     * @returns {Promise|null} JSON-decoded response
     */
    async postRequest(url, data = null) {
        let fullUrl = this.endpointsBase + url,
            requestParams = {
                method: 'POST',
                url: fullUrl,
                headers: {
                    'Accept': 'application/json, text/plain',
                    'Content-Type': 'application/json; charset=UTF-8'
                },
                data: JSON.stringify(data),
            };

        return await this._request(requestParams);
    }


    /**
     * Make POST request to send Form data to API endpoint
     * @param {string} url a relative API endpoint URL
     * @param {FormData|null} data data to send
     * @returns {Promise|null} JSON-decoded response
     */
    async postRequestFormData(url, data = null) {
        let fullUrl = this.endpointsBase + url,
            requestParams = {
                method: 'POST',
                url: fullUrl,
                headers: {
                    'Accept': 'application/json, text/plain',
                    'Content-Type': 'multipart/form-data; charset=UTF-8'
                },
                data: data
            };

        return await this._request(requestParams);
    }


    /**
     * Make PUT request to API endpoint
     * @param {string} url a relative API endpoint URL
     * @param {Object|null} data data to send
     * @returns {Promise|null} JSON-decoded response
     */
    async putRequest(url, data = null) {
        let fullUrl = this.endpointsBase + url,
            requestParams = {
                method: 'PUT',
                url: fullUrl,
                headers: {
                    'Accept': 'application/json, text/plain',
                    'Content-Type': 'application/json; charset=UTF-8'
                },
                data: JSON.stringify(data),
            };

        return await this._request(requestParams);
    }


    /**
     * Make DELETE request to API endpoint
     * @param {string} url a relative API endpoint URL
     * @returns {Promise|null} JSON-decoded response
     */
    async deleteRequest(url) {
        let fullUrl = this.endpointsBase + url,
            requestParams = {
                method: 'DELETE',
                url: fullUrl,
                headers: {
                    'Accept': 'application/json, text/plain',
                    'Content-Type': 'application/json; charset=UTF-8'
                }
            };

        return this._request(requestParams);
    }


    /**
     * Perform a request and handle errors, if any
     * @param {Object} requestParams
     * @param attempt
     * @returns {Promise<*>}
     * @private
     */
    async _request(requestParams, attempt = 0) {
        let results = null;

        requestParams = await this._refreshTokenIfNeeded(requestParams);

        if (!requestParams.hasOwnProperty('withCredentials')) {
            requestParams.withCredentials = true;
        }

        try {
            results = await axios.request(requestParams);
        } catch (e) {
            if (import.meta.env.VITE_DEBUG_MODE === 'true') {
                console.log('%cRequest error:', 'color: white; background-color: red');
                console.log('requestParams', requestParams);
                console.log('error', e);

                results = e.response;
            } else if (e.response.status === 401) {
                if (window) {
                    window.location = '/auth/logout';
                }

                return;
            } else if (attempt < 2) {
                results = await this._request(requestParams, attempt + 1);
            } else {
                results = e;
            }
        }

        return results;
    }


    /**
     * Refresh access token if needed
     */
    async _refreshTokenIfNeeded(requestParams) {
        /*if (requestParams.headers['Authorization']) {
            const accessTokenExpiresAt = localStorage.getItem('access_token_expires_at');
            const refreshToken = localStorage.getItem('refresh_token');
            const refreshTokenExpiresAt = localStorage.getItem('refresh_token_expires_at');

            if (
                accessTokenExpiresAt &&
                accessTokenExpiresAt < Math.floor(Date.now() / 1000 + 60) //add a gap of 1 minute until token expires
            ) {
                // update access token with refresh token, if possible
                if (
                    refreshToken &&
                    refreshTokenExpiresAt > Math.floor(Date.now() / 1000 + 60) //add a gap of 1 minute until token expires
                ) {
                    const newTokens = await this._refreshToken(refreshToken);
                    if (newTokens.data && newTokens.data.access_token) {
                        const accessTokenJWT = jwtDecode(newTokens.data.access_token);
                        const refreshTokenJWT = jwtDecode(newTokens.data.refresh_token);

                        localStorage.setItem('access_token', newTokens.data.access_token);
                        localStorage.setItem('access_token_expires_at', accessTokenJWT.exp);
                        localStorage.setItem('refresh_token', newTokens.data.refresh_token);
                        localStorage.setItem('refresh_token_expires_at', refreshTokenJWT.exp);

                        requestParams.headers['Authorization'] = 'Bearer ' + newTokens.data.access_token;
                    }
                } else if (accessTokenExpiresAt >= Math.floor(Date.now() / 1000)) {
                // try to update access token moving "sliding window"

                    const currentAccessToken = localStorage.getItem('access_token');
                    const newAccessToken = await this._selfUpdateToken(currentAccessToken);
                    if (newAccessToken.data && newAccessToken.data.access_token) {
                        const accessTokenJWT = jwtDecode(newAccessToken.data.access_token);

                        localStorage.setItem('access_token', newAccessToken.data.access_token);
                        localStorage.setItem('access_token_expires_at', accessTokenJWT.exp);

                        requestParams.headers['Authorization'] = 'Bearer ' + newAccessToken.data.access_token;
                    }
                }
            }
        }

        if (!requestParams.headers['X-Organization-ID']) {
            let userStored = localStorage.getItem('user');
            if (userStored) {userStored = JSON.parse(userStored);}

            if (userStored && userStored.organization.id) {
                requestParams.headers['X-Organization-ID'] = userStored.organization.id;
            }
        }*/

        return requestParams;
    }


    /**
     * Refresh access token
     */
    async _refreshToken(refreshToken) {
        const requestParams = {
            method: 'POST',
            url: this.endpointsBase + this.apiUrls.auth.refreshToken,
            headers: {
                'Accept': 'application/json, text/plain',
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + refreshToken
            }
        };

        return axios.request(requestParams);
    }


    /**
     * Refresh access token
     */
    async _selfUpdateToken(accessToken) {
        const requestParams = {
            method: 'POST',
            url: this.endpointsBase + this.apiUrls.auth.selfUpdateToken,
            headers: {
                'Accept': 'application/json, text/plain',
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + accessToken
            }
        };

        return axios.request(requestParams);
    }
}


const ApiContext = React.createContext(null);
function ApiProvider({children}) {
    const apiHelper = new ApiHelper(import.meta.env.VITE_API_URL);
    let [api, setApi] = React.useState(apiHelper);

    let value = {api};

    return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>;
}
function useApi() {return React.useContext(ApiContext);}

export {ApiProvider, useApi};